import React from 'react';

import { Spinner } from '@admin/components/spinner';

import { dateInputBounds, formatDateInputValue } from './filters_util';
import { IUnavailability, IUnavailabilityForm } from './types';
import { UnavailabilityActions, unavailabilityResource } from './unavailability_actions';

type NonrecurringTableProps = {
  unavailabilities: IUnavailability[] | null;
  form: IUnavailabilityForm | null;
  selection: IUnavailability | null;
  saving: boolean;
  onAdd(): void;
  onManage(unavailability: IUnavailability): void;
  onDestroy(unavailability: IUnavailability): void;
  onCancel(): void;
  onSave(): void;
  onFormChange(form: IUnavailabilityForm): void;
};

const NonrecurringRow: React.FC<{
  unavailability?: IUnavailability;
  form: IUnavailabilityForm | null;
  saving: boolean;
  onManage(unavailability: IUnavailability): void;
  onDestroy(unavailability: IUnavailability): void;
  onCancel(): void;
  onSave(): void;
  onFormChange(form: IUnavailabilityForm): void;
}> = ({ unavailability, form, saving, onManage, onDestroy, onCancel, onSave, onFormChange }) => {
  const current = unavailabilityResource(unavailability, form);
  const editing = !!form && (!unavailability || unavailability.id === form.id);
  const fieldID = current.id ? String(current.id) : '';
  const { min, max } = dateInputBounds();

  return (
    <tr
      id={unavailability ? `unavailability_${unavailability.id}` : undefined}
      className={editing ? 'active' : undefined}
    >
      <td className="col-zero col-nowrap text-left">
        <div className="form-inline">
          <input
            name={`unavailability[${fieldID}][start_date]`}
            type="date"
            className="form-control form-control-fit"
            min={min}
            max={max}
            disabled={!editing}
            value={formatDateInputValue(
              current.start_date ? new Date(`${current.start_date}T00:00:00.000Z`) : undefined,
            )}
            onChange={(event) =>
              onFormChange({
                ...current,
                start_date: event.target.value || null,
              })
            }
          />
          <input
            name={`unavailability[${fieldID}][until_date]`}
            type="date"
            className="form-control form-control-fit"
            min={min}
            max={max}
            disabled={!editing}
            value={formatDateInputValue(
              current.until_date ? new Date(`${current.until_date}T00:00:00.000Z`) : undefined,
            )}
            onChange={(event) =>
              onFormChange({
                ...current,
                until_date: event.target.value || null,
              })
            }
          />
        </div>
      </td>
      <td className="col-zero col-nowrap text-right">
        <UnavailabilityActions
          unavailability={unavailability}
          form={form}
          saving={saving}
          onManage={onManage}
          onDestroy={onDestroy}
          onCancel={onCancel}
          onSave={onSave}
        />
      </td>
    </tr>
  );
};

export const NonrecurringTable: React.FC<NonrecurringTableProps> = ({
  unavailabilities,
  form,
  selection,
  saving,
  onAdd,
  onManage,
  onDestroy,
  onCancel,
  onSave,
  onFormChange,
}) => {
  const nonrecurring = (unavailabilities ?? []).filter((entry) => !entry.reoccurring);
  const showNewForm = !selection && form && !form.reoccurring;

  return (
    <div className="panel availabilities-nonreoccurring">
      <div className="panel-heading">
        <div className="panel-title">Specific Time Off</div>
      </div>
      <div className="table-responsive">
        <table className="table">
          <tbody>
            {nonrecurring.map((unavailability) => (
              <NonrecurringRow
                key={unavailability.id}
                unavailability={unavailability}
                form={form}
                saving={saving}
                onManage={onManage}
                onDestroy={onDestroy}
                onCancel={onCancel}
                onSave={onSave}
                onFormChange={onFormChange}
              />
            ))}
            {showNewForm && (
              <NonrecurringRow
                form={form}
                saving={saving}
                onManage={onManage}
                onDestroy={onDestroy}
                onCancel={onCancel}
                onSave={onSave}
                onFormChange={onFormChange}
              />
            )}
          </tbody>
        </table>
      </div>
      {!unavailabilities && (
        <div className="panel-content">
          <Spinner />
        </div>
      )}
      <div className="panel-footer">
        <div className="clearfix">
          <button type="button" className="btn btn-default pull-right" onClick={onAdd}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
};
